"use client";

import Bolt from "@mui/icons-material/Bolt";
import RocketLaunch from "@mui/icons-material/RocketLaunch";
import SetMeal from "@mui/icons-material/SetMeal";
import VerifiedUser from "@mui/icons-material/VerifiedUser";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { LUXE_ACCENT } from "../luxe-shared/keyframes";

export interface ValuePropItem {
  icon: string;
  title: string;
  tone?: "primary" | "secondary" | "accent" | "blue";
  animation?: "bounce" | "pulse" | "spin" | "none";
}

export interface ValuePropsRowLuxeProps {
  items?: ValuePropItem[];
}

const iconMap: Record<string, React.ElementType> = {
  set_meal: SetMeal,
  bolt: Bolt,
  rocket_launch: RocketLaunch,
  verified_user: VerifiedUser,
};

const ValuePropsRowLuxe: React.FC<ValuePropsRowLuxeProps> = ({ items = [] }) => {
  const theme = useTheme();

  const toneStyles = (tone: ValuePropItem["tone"]) => {
    switch (tone) {
      case "secondary":
        return {
          iconBg: `${theme.palette.secondary.main}1a`,
          iconBorder: `${theme.palette.secondary.main}33`,
          color: theme.palette.secondary.main,
        };
      case "accent":
        return {
          iconBg: `${LUXE_ACCENT}1a`,
          iconBorder: `${LUXE_ACCENT}33`,
          color: LUXE_ACCENT,
        };
      case "blue":
        return {
          iconBg: "rgba(96, 165, 250, 0.1)",
          iconBorder: "rgba(96, 165, 250, 0.2)",
          color: "#3b82f6",
        };
      case "primary":
      default:
        return {
          iconBg: `${theme.palette.primary.main}1a`,
          iconBorder: `${theme.palette.primary.main}33`,
          color: theme.palette.primary.main,
        };
    }
  };

  const animSx = (animation: ValuePropItem["animation"]) => {
    if (animation === "bounce") {
      return {
        animation: "bounce 2s ease-in-out infinite",
        "@keyframes bounce": {
          "0%, 100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(-6px)" },
        },
      };
    }
    if (animation === "pulse") {
      return { animation: "pulse 2s ease-in-out infinite" };
    }
    if (animation === "spin") {
      return {
        animation: "spin 3s linear infinite",
        "@keyframes spin": {
          to: { transform: "rotate(360deg)" },
        },
      };
    }
    return {};
  };

  return (
    <Box
      component="section"
      sx={{
        py: 8,
        mt: { xs: -4, md: -5 },
        position: "relative",
        zIndex: 2,
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
        <Grid container spacing={3}>
          {items.map((item) => {
            const Icon = iconMap[item.icon] || SetMeal;
            const t = toneStyles(item.tone);
            return (
              <Grid item xs={6} md={3} key={item.title}>
                <Stack
                  alignItems="center"
                  spacing={2}
                  sx={{
                    p: 4,
                    borderRadius: "24px",
                    bgcolor: "rgba(255,255,255,0.8)",
                    backdropFilter: "blur(16px)",
                    WebkitBackdropFilter: "blur(16px)",
                    border: `1px solid ${theme.palette.primary.main}33`,
                    boxShadow: "0 10px 30px -10px rgba(0,0,0,0.05)",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    height: "100%",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      boxShadow: "0 20px 40px -15px rgba(0, 234, 211, 0.2)",
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "16px",
                      bgcolor: t.iconBg,
                      border: `1px solid ${t.iconBorder}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: t.color,
                      transition: "transform 0.3s",
                      ".MuiStack-root:hover &": { transform: "scale(1.1)" },
                      ...animSx(item.animation),
                    }}
                  >
                    <Icon sx={{ fontSize: 32 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      letterSpacing: "0.02em",
                      color: "text.primary",
                      textAlign: "center",
                    }}
                  >
                    {item.title}
                  </Typography>
                </Stack>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default ValuePropsRowLuxe;
